/**
 * SystemProsCons Component
 * Shows pros and cons for each system type based on technical data
 */

import type { SystemLayout } from './types';
import type { SystemVisualModel } from '../../../../../src/core/types';

interface SystemProsConsProps {
  layout: SystemLayout | null;
  flowRate?: number;  // For detecting low pressure scenarios
  visualModel?: SystemVisualModel;
}

// Flow rate threshold below which a buffer tank is recommended (L/min)
// Heat pumps require stable flow rates; below 12 L/min, a buffer/accumulator
// helps smooth out flow variations and prevents short-cycling
const BUFFER_TANK_FLOW_THRESHOLD = 12;

/**
 * Get system-specific pros and cons from visual model or layout
 */
function getSystemProsCons(layout: SystemLayout | null, flowRate: number | undefined, visualModel?: SystemVisualModel) {
  // If visual model is available, use its characteristics
  if (visualModel) {
    const pros = [visualModel.characteristics.primaryBenefit];
    const cons = [visualModel.characteristics.primaryTradeoff];
    
    // Add capacity-based pros/cons
    if (visualModel.capacitySignals.simultaneousOutlets > 1) {
      pros.push(`Can supply ${visualModel.capacitySignals.simultaneousOutlets} outlets simultaneously`);
    } else {
      cons.push('Limited to one outlet at a time');
    }
    
    if (visualModel.capacitySignals.suitableForPowerShower) {
      pros.push('High pressure suitable for power showers');
    }
    
    if (visualModel.thermalProfile.comfortStability === 'very_high') {
      pros.push('Excellent temperature stability');
    } else if (visualModel.thermalProfile.comfortStability === 'low') {
      cons.push('May experience temperature fluctuations');
    }
    
    // Add response time info
    if (visualModel.thermalProfile.dhwResponseSeconds < 10) {
      pros.push('Instant hot water at taps');
    } else if (visualModel.thermalProfile.dhwResponseSeconds > 30) {
      cons.push('Slight delay for hot water delivery');
    }
    
    return { pros, cons };
  }
  
  // Fallback to legacy layout-based logic
  if (!layout) {
    return {
      pros: ['Reliable heating and hot water'],
      cons: ['Consult with installer for specific details'],
    };
  }
  
  const systemType = layout.systemType.toLowerCase();
  
  if (systemType.indexOf('combi') >= 0) {
    return {
      pros: [
        'Space-saving - no cylinder required',
        'Unlimited hot water (never runs out)',
        'Lower installation cost',
        'Instant hot water at taps',
      ],
      cons: [
        'Limited to one outlet at a time',
        'Performance depends on mains pressure',
        'Flow rate may be insufficient for large baths',
        'Cannot store hot water for peak demand',
      ],
    };
  }
  
  if (systemType.indexOf('regular') >= 0) {
    return {
      pros: [
        'Compatible with older heating systems',
        'Good for properties with low mains pressure',
        'Can supply multiple outlets simultaneously',
        'Separate cold water storage tank',
      ],
      cons: [
        'Requires loft space for tanks',
        'Lower water pressure (gravity-fed)',
        'Takes up space in airing cupboard',
        'Limited by cylinder capacity',
      ],
    };
  }
  
  if (systemType.indexOf('system') >= 0) {
    return {
      pros: [
        'High-pressure hot water at all outlets',
        'Can supply 2+ showers simultaneously',
        'No loft tanks required',
        'Faster heating recovery time',
      ],
      cons: [
        'Requires adequate mains pressure (1.5+ bar)',
        'Annual unvented cylinder inspection required',
        'Higher installation cost than combi',
        'Space required for cylinder',
      ],
    };
  }
  
  if (systemType.indexOf('heat pump') >= 0 || systemType.indexOf('heat_pump') >= 0) {
    // Check if buffer/accumulator is needed based on flow rate
    const needsBuffer = flowRate && flowRate < BUFFER_TANK_FLOW_THRESHOLD;
    
    const pros = [
      'Up to 400% efficiency (COP 4.0)',
      'Extremely low running costs',
      'Zero carbon at point of use',
      'Eligible for government grants (BUS)',
      'Future-proof technology',
    ];
    
    const cons = [
      'Higher upfront installation cost',
      'Requires external space for ASHP unit',
      'May need radiator upgrades for optimal performance',
      'Best suited to well-insulated properties',
    ];
    
    if (needsBuffer) {
      pros.push('Buffer tank smooths out flow variations');
      cons.push(`Buffer/accumulator required for low flow rates (<${BUFFER_TANK_FLOW_THRESHOLD} L/min)`);
    }
    
    return { pros, cons };
  }
  
  // Default fallback
  return {
    pros: ['Reliable heating and hot water'],
    cons: ['Consult with installer for specific details'],
  };
}

export function SystemProsCons({ layout, flowRate, visualModel }: SystemProsConsProps) {
  const { pros, cons } = getSystemProsCons(layout, flowRate, visualModel);
  
  return (
    <div style={{ 
      padding: '16px',
      border: '2px solid #cbd5e1',
      borderRadius: '8px',
      background: 'white',
      marginTop: '16px',
    }}>
      <h3 style={{ 
        fontSize: '18px', 
        fontWeight: 600, 
        marginBottom: '16px',
        textAlign: 'center',
        color: '#1e293b'
      }}>
        System Summary
      </h3>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
      }}>
        {/* Pros */}
        <div>
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#047857',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <span style={{ fontSize: '18px' }}>✓</span>
            Advantages
          </div>
          
          <ul style={{
            margin: 0,
            padding: '0 0 0 20px',
            listStyle: 'disc',
          }}>
            {pros.map((pro, idx) => (
              <li 
                key={idx}
                style={{
                  fontSize: '12px',
                  color: '#334155',
                  marginBottom: '6px',
                  lineHeight: '1.4',
                }}
              >
                {pro}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Cons */}
        <div>
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#dc2626',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <span style={{ fontSize: '18px' }}>!</span>
            Considerations
          </div>
          
          <ul style={{
            margin: 0,
            padding: '0 0 0 20px',
            listStyle: 'disc',
          }}>
            {cons.map((con, idx) => (
              <li 
                key={idx}
                style={{
                  fontSize: '12px',
                  color: '#334155',
                  marginBottom: '6px',
                  lineHeight: '1.4',
                }}
              >
                {con}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
